import { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import LoadingIcon from 'src/assets/icons/loader.svg';

import type { Size } from 'src/types/common';

export interface AvatarProps {
  firstName?: string | null;
  lastName?: string | null;
  img?: string | null;
  size?: Size | 'large';
  loading?: boolean;
  /**
   * Announces the user's computed full name for the avatar image.
   * Use this only when the avatar is the primary identity cue in the UI,
   * such as standalone selections or lists without adjacent identifying text.
   */
  announceName?: boolean;
  className?: string;
}

const Avatar = ({
  size = 'medium',
  loading = false,
  firstName,
  lastName,
  img,
  announceName = false,
  className,
}: AvatarProps) => {
  const first = firstName?.length ? firstName.charAt(0) : '-';
  const last = lastName?.length ? lastName.charAt(0) : '-';
  const name = first.concat(last).toUpperCase();
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  const imageAlt = announceName ? fullName : '';

  return (
    <Container
      size={size}
      loading={loading}
      className={twMerge(
        `${img ? 'border-surface-ghost-45' : 'border-transparent'} `,
        className
      )}
    >
      {img ? (
        <img
          className="flex-shrink-0 rounded-full w-full h-full"
          src={img}
          alt={imageAlt}
        />
      ) : (
        <span className="flex items-center justify-center flex-shrink-0 bg-secondary-ghost-alt rounded-full font-bold text-secondary-ghost-alt-75 w-full h-full">
          {name}
        </span>
      )}
    </Container>
  );
};

const Container = ({
  loading = false,
  className,
  children,
  size,
}: Omit<AvatarProps, 'firstName' | 'lastName'> & {
  children?: ReactNode;
}) => {
  const getSize = (s: typeof size) => {
    if (s === 'small') return 'w-7 h-7 text-xs';
    if (s === 'large') return 'w-28 h-28 text-5xl';

    return 'w-9 h-9';
  };

  return (
    <div
      className={twMerge(
        `inline-flex items-center justify-center rounded-full flex-shrink-0 p-px border-2 ${getSize(size)}`,
        className
      )}
    >
      {loading ? (
        <>
          <LoadingIcon
            aria-hidden="true"
            className="w-10 h-10 text-secondary-ghost-alt-60 animate-spin"
          />
          <span role="status" className="sr-only">
            Loading avatar
          </span>
        </>
      ) : (
        children
      )}
    </div>
  );
};

export default Avatar;
